/**
 * Shared UI component for UI Form Field.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';

import { UiFormFieldAppearance, UiFormFieldType } from './ui-form-field.types';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

/**
 * COMPONENT: Ui Form Field
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-form-field',
  standalone: true,
  imports: [InputTextModule, TextareaModule, TooltipModule, UiLabelTranslatePipe],
  templateUrl: './ui-form-field.component.html',
  styleUrl: './ui-form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiFormFieldComponent {
  /** STATE OWNER: passwordVisible documents the signal state boundary used by this file. */
  readonly passwordVisible = signal(false);
  /** STATE OWNER: dragOver documents the signal state boundary used by this file. */
  readonly dragOver = signal(false);
  readonly selectedFiles = signal<File[]>([]);

  readonly appearance = input<UiFormFieldAppearance>('outline');
  /** PARENT-CHILD CONTRACT: label documents the input state boundary used by this file. */
  readonly label = input('');
  readonly value = input<string>('');
  readonly type = input<UiFormFieldType>('text');
  /** PARENT-CHILD CONTRACT: placeholder documents the input state boundary used by this file. */
  readonly placeholder = input('');
  /** PARENT-CHILD CONTRACT: id documents the input state boundary used by this file. */
  readonly id = input('');
  /** PARENT-CHILD CONTRACT: disabled documents the input state boundary used by this file. */
  readonly disabled = input(false);
  /** PARENT-CHILD CONTRACT: required documents the input state boundary used by this file. */
  readonly required = input(false);
  /** PARENT-CHILD CONTRACT: readonly documents the input state boundary used by this file. */
  readonly readonly = input(false);
  /** PARENT-CHILD CONTRACT: error documents the input state boundary used by this file. */
  readonly error = input('');
  /** PARENT-CHILD CONTRACT: hint documents the input state boundary used by this file. */
  readonly hint = input('');
  /** PARENT-CHILD CONTRACT: rows documents the input state boundary used by this file. */
  readonly rows = input(3);
  readonly min = input<string>('');
  readonly max = input<string>('');
  readonly step = input<string>('');
  /** PARENT-CHILD CONTRACT: showPasswordLabel documents the input state boundary used by this file. */
  readonly showPasswordLabel = input('shared.form.showPassword');
  /** PARENT-CHILD CONTRACT: hidePasswordLabel documents the input state boundary used by this file. */
  readonly hidePasswordLabel = input('shared.form.hidePassword');
  readonly accept = input<string>('');
  /** PARENT-CHILD CONTRACT: multiple documents the input state boundary used by this file. */
  readonly multiple = input(false);
  readonly maxFileSizeMb = input<number>(0);
  readonly valueChange = output<string>();
  readonly filesChange = output<File[]>();

  /** STATE OWNER: isPasswordField documents the computed state boundary used by this file. */
  readonly isPasswordField = computed(() => this.type() === 'password');
  /** STATE OWNER: isFileField documents the computed state boundary used by this file. */
  readonly isFileField = computed(() => this.type() === 'file');
  /** STATE OWNER: isPlaceholderLong documents the computed state boundary used by this file. */
  readonly isPlaceholderLong = computed(() => this.placeholder().length > 20);
  /** STATE OWNER: showPlaceholderHint documents the computed state boundary used by this file. */
  readonly showPlaceholderHint = computed(() => this.isPlaceholderLong() && !this.isFileField());
  /** STATE OWNER: resolvedInputType documents the computed state boundary used by this file. */
  readonly resolvedInputType = computed(() => (this.isPasswordField() && this.passwordVisible() ? 'text' : this.type()));
  /** STATE OWNER: passwordToggleLabel documents the computed state boundary used by this file. */
  readonly passwordToggleLabel = computed(() => (this.passwordVisible() ? this.hidePasswordLabel() : this.showPasswordLabel()));
  /** STATE OWNER: passwordToggleIcon documents the computed state boundary used by this file. */
  readonly passwordToggleIcon = computed(() => (this.passwordVisible() ? 'visibility_off' : 'visibility'));
  /** STATE OWNER: fileDropLabel documents the computed state boundary used by this file. */
  readonly fileDropLabel = computed(() => this.placeholder() || 'shared.form.dropFiles');

  /**
   * BUSINESS LOGIC: onValueChange
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onValueChange(field: HTMLInputElement | HTMLTextAreaElement): void {
    const normalized = this.normalizeValue(field.value);

    if (field.value !== normalized) {
      field.value = normalized;
    }

    this.valueChange.emit(normalized);
  }

  /**
   * BUSINESS LOGIC: togglePasswordVisibility
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  togglePasswordVisibility(): void {
    if (!this.isPasswordField() || this.disabled()) {
      return;
    }

    this.passwordVisible.update((visible) => !visible);
  }

  /**
   * BUSINESS LOGIC: onFileInputChange
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.applyFiles(Array.from(input.files ?? []));
  }

  /**
   * BUSINESS LOGIC: onDragOver
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onDragOver(event: DragEvent): void {
    if (this.disabled()) return;
    event.preventDefault();
    this.dragOver.set(true);
  }

  /**
   * BUSINESS LOGIC: onDragLeave
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onDragLeave(): void {
    this.dragOver.set(false);
  }

  /**
   * BUSINESS LOGIC: onDrop
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onDrop(event: DragEvent): void {
    if (this.disabled()) return;
    event.preventDefault();
    this.dragOver.set(false);
    const files = Array.from(event.dataTransfer?.files ?? []);
    this.applyFiles(this.multiple() ? files : files.slice(0, 1));
  }

  /**
   * BUSINESS LOGIC: removeFile
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  removeFile(index: number): void {
    this.selectedFiles.update((files) => files.filter((_, i) => i !== index));
    this.filesChange.emit(this.selectedFiles());
  }

  /**
   * BUSINESS LOGIC: applyFiles
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private applyFiles(files: File[]): void {
    const maxBytes = this.maxFileSizeMb() * 1024 * 1024;
    const filtered = maxBytes > 0 ? files.filter((f) => f.size <= maxBytes) : files;
    this.selectedFiles.set(this.multiple() ? [...this.selectedFiles(), ...filtered] : filtered);
    this.filesChange.emit(this.selectedFiles());
  }

  /**
   * BUSINESS LOGIC: normalizeValue
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeValue(value: string): string {
    if (this.type() === 'tel') {
      return value.replace(/\D+/g, '');
    }

    if (this.type() !== 'number') {
      return value;
    }

    const raw = value.replace(/[^0-9.-]/g, '');
    const hasLeadingMinus = raw.startsWith('-');
    const unsigned = hasLeadingMinus ? raw.slice(1) : raw;
    const [integerPart = '', ...decimalParts] = unsigned.split('.');
    const normalizedInteger = integerPart.replace(/-/g, '');
    const normalizedDecimal = decimalParts.join('').replace(/[.-]/g, '');
    const decimalSuffix = unsigned.includes('.') ? `.${normalizedDecimal}` : '';

    return `${hasLeadingMinus ? '-' : ''}${normalizedInteger}${decimalSuffix}`;
  }
}
